// ── Microsoft Teams / Graph Permission Registry ─────────────────────────────
// Canonical registry of the Teams + Microsoft Graph permissions the bot can
// request. Powers the interactive permission-selection UI and manifest
// generation. Mirrors the shape of `slack-scopes.ts`.
//
// Each permission carries a `grant_type`:
//
// - `rsc`         — Resource-Specific Consent, declared in the Teams app
//                   manifest `authorization.permissions.resourceSpecific`
//                   block. Granted per-team by a team owner; no tenant-admin
//                   consent required.
// - `application` — Application permission granted via Entra (Azure AD)
//                   tenant-admin consent. Used for the Bot Framework
//                   `client_credentials` flow.
// - `delegated`   — Delegated permission granted by a user via interactive
//                   sign-in. Rarely used by autonomous bots but listed for
//                   completeness (e.g. Files.Read.All can be delegated).
//
// Reference:
//   https://learn.microsoft.com/en-us/microsoftteams/platform/graph-api/rsc/resource-specific-consent
//   https://learn.microsoft.com/en-us/graph/permissions-reference

import type { MsTeamsPermission } from '../types/channel-config.js';

export type MsTeamsScopeCategory =
  | 'messaging'
  | 'files'
  | 'meetings'
  | 'team-management'
  | 'user';

export type MsTeamsScopeGrantType = 'rsc' | 'application' | 'delegated';

export type MsTeamsScopeRisk = 'low' | 'medium' | 'high';

export interface MsTeamsScopeDefinition {
  scope: MsTeamsPermission;
  name: string;
  description: string;
  category: MsTeamsScopeCategory;
  risk: MsTeamsScopeRisk;
  grant_type: MsTeamsScopeGrantType;
}

export const MSTEAMS_SCOPE_REGISTRY: readonly MsTeamsScopeDefinition[] = [
  // ── Messaging ────────────────────────────────────────────────────────────
  {
    scope: 'ChannelMessage.Read.Group',
    name: 'Read Channel Messages',
    description:
      'Read messages in Teams channels the bot has been added to (RSC, per-team).',
    category: 'messaging',
    risk: 'medium',
    grant_type: 'rsc',
  },
  {
    scope: 'ChannelMessage.Send.Group',
    name: 'Send Channel Messages',
    description: 'Post messages to Teams channels the bot has been added to.',
    category: 'messaging',
    risk: 'low',
    grant_type: 'rsc',
  },
  {
    scope: 'ChatMessage.Read.Chat',
    name: 'Read Chat Messages',
    description: 'Read messages in 1:1 and group chats the bot is part of.',
    category: 'messaging',
    risk: 'high',
    grant_type: 'rsc',
  },
  {
    scope: 'Chat.ReadWrite',
    name: 'Read and Write Chats',
    description: 'Create, read, and update 1:1 and group chats the bot is part of.',
    category: 'messaging',
    risk: 'high',
    grant_type: 'application',
  },
  {
    scope: 'TeamsActivity.Send',
    name: 'Send Activity Notifications',
    description:
      'Send proactive activity feed notifications (toasts) to users in the tenant.',
    category: 'messaging',
    risk: 'medium',
    grant_type: 'application',
  },

  // ── Files ────────────────────────────────────────────────────────────────
  {
    scope: 'Files.Read.All',
    name: 'Read All Files',
    description:
      'Read files the user can access in OneDrive and SharePoint (no write).',
    category: 'files',
    risk: 'medium',
    grant_type: 'application',
  },
  {
    scope: 'Files.ReadWrite.All',
    name: 'Read and Write All Files',
    description:
      'Read, create, update, and delete files in OneDrive and SharePoint. Required for `uploadTeamsFile`.',
    category: 'files',
    risk: 'high',
    grant_type: 'application',
  },

  // ── Meetings ─────────────────────────────────────────────────────────────
  {
    scope: 'ChannelMeeting.ReadBasic.Group',
    name: 'Read Channel Meeting Info',
    description:
      'Read basic info (title, time, organiser) of channel meetings in teams the bot has been added to.',
    category: 'meetings',
    risk: 'low',
    grant_type: 'rsc',
  },
  {
    scope: 'OnlineMeetings.ReadWrite.All',
    name: 'Read and Write Online Meetings',
    description:
      'Create, read, update, and delete Teams online meetings for any user in the tenant.',
    category: 'meetings',
    risk: 'high',
    grant_type: 'application',
  },

  // ── Team Management ──────────────────────────────────────────────────────
  {
    scope: 'Team.ReadBasic.All',
    name: 'Read Basic Team Info',
    description: 'List the teams the bot has been added to and read their basic info.',
    category: 'team-management',
    risk: 'low',
    grant_type: 'application',
  },
  {
    scope: 'TeamMember.Read.Group',
    name: 'Read Team Members',
    description:
      'Read the membership list of teams the bot has been added to (RSC, per-team).',
    category: 'team-management',
    risk: 'medium',
    grant_type: 'rsc',
  },
  {
    scope: 'ChannelSettings.Read.All',
    name: 'Read Channel Settings',
    description: 'Read settings of channels the bot has been added to.',
    category: 'team-management',
    risk: 'low',
    grant_type: 'rsc',
  },
  {
    scope: 'ChannelSettings.ReadWrite.All',
    name: 'Manage Channel Settings',
    description:
      'Read and update settings of channels the bot has been added to (rename, description, moderation).',
    category: 'team-management',
    risk: 'high',
    grant_type: 'rsc',
  },

  // ── User ─────────────────────────────────────────────────────────────────
  {
    scope: 'User.Read.All',
    name: 'Read All User Profiles',
    description:
      'Read full profile info (display name, email, job title) of users in the tenant.',
    category: 'user',
    risk: 'medium',
    grant_type: 'application',
  },

  // ── App Lifecycle ────────────────────────────────────────────────────────
  {
    scope: 'TeamsAppInstallation.ReadWriteForUser.All',
    name: 'Install/Uninstall App for User',
    description:
      'Install, upgrade, and uninstall the Teams app for users in the tenant — required for proactive install before first DM.',
    category: 'user',
    risk: 'high',
    grant_type: 'application',
  },
] as const;

/** All categories in display order. */
export const MSTEAMS_SCOPE_CATEGORIES: readonly MsTeamsScopeCategory[] = [
  'messaging',
  'files',
  'meetings',
  'team-management',
  'user',
] as const;

/** Human-readable category labels. */
export const MSTEAMS_SCOPE_CATEGORY_LABELS: Record<MsTeamsScopeCategory, string> = {
  messaging: 'Messaging',
  files: 'Files',
  meetings: 'Meetings',
  'team-management': 'Team Management',
  user: 'User',
};

/** Default recommended permissions for a standard Teams bot. */
const DEFAULT_PERMISSIONS: readonly MsTeamsPermission[] = [
  'ChannelMessage.Read.Group',
  'ChannelMessage.Send.Group',
  'ChatMessage.Read.Chat',
  'Chat.ReadWrite',
  'Team.ReadBasic.All',
  'TeamMember.Read.Group',
  'ChannelSettings.Read.All',
  'Files.ReadWrite.All',
  'User.Read.All',
  'TeamsAppInstallation.ReadWriteForUser.All',
] as const;

/** Returns the recommended default set of Teams permissions. */
export function getDefaultMsTeamsPermissions(): MsTeamsPermission[] {
  return [...DEFAULT_PERMISSIONS];
}

/** Returns scope definitions grouped by category, preserving registry order. */
export function getMsTeamsScopesByCategory(): Map<
  MsTeamsScopeCategory,
  MsTeamsScopeDefinition[]
> {
  const map = new Map<MsTeamsScopeCategory, MsTeamsScopeDefinition[]>();
  for (const cat of MSTEAMS_SCOPE_CATEGORIES) {
    map.set(cat, []);
  }
  for (const def of MSTEAMS_SCOPE_REGISTRY) {
    map.get(def.category)!.push(def);
  }
  return map;
}

/** Look up a scope definition by permission string. */
export function getMsTeamsScopeDefinition(
  scope: MsTeamsPermission,
): MsTeamsScopeDefinition | undefined {
  return MSTEAMS_SCOPE_REGISTRY.find((s) => s.scope === scope);
}

/**
 * Returns scope definitions partitioned by `grant_type`. Useful for the
 * manifest generator (RSC entries vs Entra application permissions) and the
 * provisioning UI (which surfaces consent steps differently per grant type).
 */
export function partitionMsTeamsScopes(
  scopes: readonly MsTeamsPermission[],
): {
  rsc: MsTeamsScopeDefinition[];
  application: MsTeamsScopeDefinition[];
  delegated: MsTeamsScopeDefinition[];
} {
  const rsc: MsTeamsScopeDefinition[] = [];
  const application: MsTeamsScopeDefinition[] = [];
  const delegated: MsTeamsScopeDefinition[] = [];
  for (const scope of scopes) {
    const def = getMsTeamsScopeDefinition(scope);
    if (!def) continue;
    if (def.grant_type === 'rsc') rsc.push(def);
    else if (def.grant_type === 'application') application.push(def);
    else delegated.push(def);
  }
  return { rsc, application, delegated };
}

/** Preset permission sets for CLI --preset flag. Matches the Slack equivalent. */
export const MSTEAMS_SCOPE_PRESETS = {
  minimal: [
    'ChannelMessage.Read.Group',
    'ChannelMessage.Send.Group',
    'Team.ReadBasic.All',
  ] as MsTeamsPermission[],

  standard: [...DEFAULT_PERMISSIONS] as MsTeamsPermission[],

  full: MSTEAMS_SCOPE_REGISTRY.map((s) => s.scope) as MsTeamsPermission[],
} as const;
